import { useState } from 'preact/hooks';
import { type CheckoutSessionDto, confirmSession, pollForRedirectUrl } from './api.js';
import { buildConsentPayload, isPayButtonEnabled } from './confirm-gate.js';
import { flushDomRecorder } from './dom-recorder.js';
import { replayClient } from './replay-client.js';
import type { RiskSessionResponse } from './risk-collect.js';

export function ConfirmPanel(props: {
  session: CheckoutSessionDto;
  /** Pay-button label — passed from the tenant's CheckoutStrategy.display.payLabel. */
  payLabel?: string;
  /** Footnote text under the pay button. */
  payFootnote?: string;
  /**
   * The payment method the shopper picked on the Method tiles. Must be
   * non-null by the time this component renders — App.tsx auto-selects a
   * default and the Lookup step is gated on a non-null selection. We
   * fail-loud rather than silently fall through to a hardcoded rail.
   */
  paymentMethod: string | null;
  /**
   * Risk session opened by App.tsx on first paint. The recorder is already
   * running by the time the shopper reaches this panel — we just read the
   * token here for verification at confirm time.
   */
  risk: RiskSessionResponse | null;
  /** Soft error from the App-level risk-session open (rendered as a hint). */
  riskError: string | null;
  /** Effective tenant id — forwarded to confirmSession as X-Tenant-Id so the
   *  server resolves the session under the right tenant (bd confirm-tenant-header). */
  tenant: string;
  onError: (msg: string) => void;
}) {
  const { session, risk, riskError } = props;
  const [accepted, setAccepted] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [duplicateAcknowledged, setDuplicateAcknowledged] = useState(false);

  const allRequiredAccepted = session.consents
    .filter((c) => c.required)
    .every((c) => accepted[c.id]);

  // bd fwk — derived via pure helpers in confirm-gate.ts so the predicate
  // stack is testable without mounting Preact.
  const payButtonEnabled = isPayButtonEnabled({
    allRequiredAccepted,
    submitting,
    risk,
    duplicateAcknowledged,
  });

  async function pay(): Promise<void> {
    if (!payButtonEnabled || !risk) return;
    if (!props.paymentMethod) {
      // Defence-in-depth — App.tsx + Lookup already prevent this, but if we
      // ever land here the symptom (a 422 from the API) is the right outcome
      // for an unrecoverable state. Surface a clear message rather than
      // silently picking a rail.
      props.onError('Select a payment method before continuing.');
      return;
    }
    setSubmitting(true);
    replayClient.mark('confirm.clicked', {
      amount_minor: session.amount.value,
      currency: session.amount.currency,
      risk_decision: risk.decision,
      payment_method: props.paymentMethod,
    });
    try {
      const r = await confirmSession(
        session.id,
        {
          payment_method: props.paymentMethod,
          // bd fwk — buildConsentPayload stamps every accepted consent with
          // the SAME acceptedAt timestamp (one click ⇒ one interaction in
          // the audit trail).
          consents: buildConsentPayload(session.consents, accepted, new Date().toISOString()),
        },
        risk.token || undefined,
        props.tenant,
      );
      // In cloud the authorization runs async under Step Functions, so confirm
      // returns AUTHORIZING with no redirect_url; the SaferPay HPP URL lands on
      // the PI moments later. Poll the API for it rather than expect it inline
      // (bd asb). When confirm already carried the URL (sync/local path) we use
      // it directly.
      const redirectUrl = r.redirect_url ?? (await pollForRedirectUrl(session.id, props.tenant));
      if (redirectUrl) {
        replayClient.mark('redirect.followed', { has_url: true });
        // Flush both recorders BEFORE navigating away — pagehide is too
        // late for the dom-events fetch (sendBeacon is body-size capped
        // and the initial DOM snapshot can blow through the limit).
        await Promise.all([replayClient.flush(), flushDomRecorder()]);
        window.location.href = redirectUrl;
        return;
      }
      props.onError(`Unexpected response: status=${r.status}, no redirect URL`);
    } catch (e) {
      props.onError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  function acknowledgeDuplicate(): void {
    setDuplicateAcknowledged(true);
    replayClient.mark('duplicate.warning_acknowledged');
  }

  const amount = formatMoney(session.amount.value, session.amount.currency);

  return (
    <div class="bg-surface-container-lowest border border-outline-variant rounded-xl p-4 sm:p-6 lg:p-8 shadow-sm space-y-4 lg:space-y-5">
      <div class="bg-surface-container-low rounded-lg p-3 text-body-sm space-y-1.5">
        <Row label="Applicant" value={session.display.applicant_name ?? '—'} />
        <Row label="Visa class" value={session.display.visa_class ?? '—'} />
        <Row label="Expires" value={session.display.expires_at?.split('T')[0] ?? '—'} />
        <div class="border-t border-outline-variant my-2" />
        <div class="flex justify-between text-h3 font-semibold">
          <span>Total</span>
          <span>{amount}</span>
        </div>
      </div>
      <div class="space-y-2 pt-2">
        {session.consents.map((c) => (
          <label key={c.id} class="flex items-start gap-2 text-body-sm cursor-pointer">
            <input
              type="checkbox"
              class="mt-0.5"
              checked={!!accepted[c.id]}
              onChange={(e) =>
                setAccepted((s) => ({
                  ...s,
                  [c.id]: (e.currentTarget as HTMLInputElement).checked,
                }))
              }
            />
            <span>
              {(c.label as { en?: string }).en ?? c.id}{' '}
              <a
                href={c.terms_url}
                target="_blank"
                rel="noreferrer"
                class="text-primary underline"
              >
                (terms)
              </a>
              {c.required && <span class="text-error ml-1">*</span>}
            </span>
          </label>
        ))}
      </div>
      {riskError && (
        <p class="text-[11px] text-error">
          We could not verify your session. Please refresh and try again.
        </p>
      )}
      {risk?.decision === 'BLOCK' && (
        <p class="text-[11px] text-error">
          This checkout has been blocked. Please contact support if you believe this is an error.
        </p>
      )}
      {risk?.possible_duplicate && (
        <div class="p-3 border-l-4 border-warning bg-warning-container/40 text-on-warning-container rounded-r space-y-2">
          <p class="text-body-sm font-semibold">
            We noticed a similar recent payment.
          </p>
          <p class="text-[11px] text-on-surface-variant">
            Roughly {Math.round(risk.possible_duplicate.delta_seconds / 60)} minute
            {risk.possible_duplicate.delta_seconds >= 120 ? 's' : ''} ago a payment
            of the same amount was made from this {risk.possible_duplicate.matched_on_key}.
            Continue only if you are sure this is a separate transaction.
          </p>
          {!duplicateAcknowledged && (
            <button
              type="button"
              class="text-[11px] underline text-primary"
              onClick={acknowledgeDuplicate}
            >
              Continue anyway
            </button>
          )}
          {duplicateAcknowledged && (
            <p class="text-[11px] text-on-surface-variant">
              Acknowledged — proceed to pay below.
            </p>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={pay}
        disabled={!payButtonEnabled}
        class={`w-full px-4 py-2.5 text-xs uppercase tracking-widest font-semibold rounded transition-opacity ${
          payButtonEnabled
            ? 'bg-primary text-on-primary hover:opacity-90'
            : 'bg-outline-variant text-on-surface-variant cursor-not-allowed'
        }`}
      >
        {submitting ? 'Redirecting…' : !risk ? 'Verifying…' : (props.payLabel ?? 'Pay securely')}
      </button>
      {props.payFootnote && (
        <p class="text-[10px] text-on-surface-variant text-center">{props.payFootnote}</p>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div class="flex justify-between">
      <span class="text-on-surface-variant">{label}</span>
      <span class="font-medium">{value}</span>
    </div>
  );
}

function formatMoney(value: string, currency: string): string {
  const cents = BigInt(value);
  const major = Number(cents) / 100;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(major);
  } catch {
    return `${currency} ${major.toFixed(2)}`;
  }
}

